import { createRootRoute, Outlet, useLocation } from "@tanstack/react-router";

import { AppShell } from "../components/AppShell";
import Login from "../components/Login";
import { Spinner } from "../components/ui";
import { t } from "../i18n";
import { trpc } from "../lib/trpc";

export const Route = createRootRoute({
  component: RootRoute,
});

// Paths that render even when the user is unauthenticated. Required for
// registration, password recovery, the email-verification link in signup
// emails, and the legal pages (privacy notice + ToS — users must be able
// to read these BEFORE signing up to satisfy GDPR Art. 13). The Outlet
// handles routing within these pages.
const PUBLIC_PREFIXES = [
  "/register",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
  "/personvern",
  "/vilkar",
] as const;

function isPublicPath(path: string): boolean {
  return PUBLIC_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));
}

function RootRoute() {
  const location = useLocation();
  const publicRoute = isPublicPath(location.pathname);
  const me = trpc.auth.me.useQuery(undefined, {
    retry: false,
    throwOnError: false,
  });

  if (me.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size="md" label={t.common.loading} />
      </div>
    );
  }

  // Public routes render bare — no AppShell — so unauthenticated users can
  // sign up / verify / reset their password without the app chrome.
  if (publicRoute) {
    return <Outlet />;
  }

  if (me.isError || !me.data) {
    return <Login />;
  }

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}
